import {
  buildResolvedTimeline,
  getEffectiveDuration as getResolvedEffectiveDuration,
  type TimelineMode,
} from '@/lib/timeline'

export function getEffectiveDuration(clip: { duration: number; trimStart?: number; trimEnd?: number; speed?: number }) {
  return getResolvedEffectiveDuration(clip)
}

export function isImageOnlyClip(clip: { generationMode?: string; firstFrameUrl?: string; videoUrl?: string }): boolean {
  if (clip.generationMode !== 'uploaded') return false
  if (clip.firstFrameUrl && clip.videoUrl && clip.firstFrameUrl === clip.videoUrl) return true
  return false
}

export interface TimelineClip {
  _id: string
  duration: number
  /** Product-demo scene payload — clip renders synthetically (no videoUrl). */
  demoScene?: import('@/remotion/demo/types').DemoScenePayload
  trimStart?: number
  trimEnd?: number
  speed?: number
  videoUrl?: string
  storyboardImageUrl?: string
  firstFrameUrl?: string
  startKeyframeUrl?: string
  endKeyframeUrl?: string
  videoStatus: string
  generationMode?: string
  nativeAudioEnabled?: boolean
  clipAudio?: {
    volume?: number
    mute?: boolean
    fadeIn?: number
    fadeOut?: number
    volumeEnvelope?: Array<{ time: number; volume: number }>
  }
  transcription?: { words?: Array<{ w: string; s: number; e: number }>; text?: string }
  solidColor?: string
  linkGroupId?: string
  timelineStartTime?: number
  transition?: { type: string; duration: number }
  // Source video dimensions
  sourceWidth?: number
  sourceHeight?: number
  // Spatial positioning (percentage-based, 0-100)
  spatialX?: number
  spatialY?: number
  spatialWidth?: number
  spatialHeight?: number
  spatialRotation?: number
  spatialZIndex?: number
  spatialOpacity?: number
  trackIndex?: number
  effects?: {
    fadeIn?: { color: string; duration: number }
    fadeOut?: { color: string; duration: number }
    colorGrade?: string
    colorGradeIntensity?: number
    letterbox?: boolean
    vignette?: number
    grain?: number
    brightness?: number
    contrast?: number
    saturation?: number
    hueRotate?: number
    blur?: number
    bloom?: number
    chromaticAberration?: number
    vhs?: boolean
    shake?: number
    kenBurns?: {
      startScale: number
      endScale: number
      startX?: number
      startY?: number
      endX?: number
      endY?: number
    }
    animationKeyframes?: {
      tracks: Array<{
        property: string
        keyframes: Array<{
          time: number
          value: number
          interpolation: string
          easing: {
            preset: string
            custom_bezier?: { x1: number; y1: number; x2: number; y2: number }
          }
        }>
      }>
    }
  }
}

export interface TimelineEntry {
  clipIndex: number
  clip: TimelineClip
  globalStart: number
  globalEnd: number
  videoStartOffset: number
  effectiveDuration: number
}

export interface TimelineModel {
  entries: TimelineEntry[]
  totalDuration: number
}

export function buildTimelineModel(clips: TimelineClip[], options?: { mode?: TimelineMode | null }): TimelineModel {
  return buildResolvedTimeline(clips, options)
}

export type { TimelineMode } from '@/lib/timeline'

export interface ActiveClipInfo {
  entry: TimelineEntry
  localTime: number
  alpha: number
  isTransitionOut?: boolean
}

export interface TransitionInfo {
  type: string
  progress: number
  duration: number
}

export function getActiveClips(model: TimelineModel, globalTime: number): {
  clips: ActiveClipInfo[]
  transition?: TransitionInfo
} {
  const active: ActiveClipInfo[] = []
  let transition: TransitionInfo | undefined

  for (let i = 0; i < model.entries.length; i++) {
    const entry = model.entries[i]
    if (globalTime < entry.globalStart || globalTime >= entry.globalEnd) continue

    const localTime = globalTime - entry.globalStart

    // Check if we're in a transition zone with the *next* clip
    const nextEntry = model.entries[i + 1]
    if (nextEntry && globalTime >= nextEntry.globalStart) {
      // We're in the overlap region — this clip is transitioning out
      const transitionDef = nextEntry.clip.transition!
      const transitionStart = nextEntry.globalStart
      const progress = Math.min(1, Math.max(0, (globalTime - transitionStart) / transitionDef.duration))

      transition = { type: transitionDef.type, progress, duration: transitionDef.duration }

      active.push({
        entry,
        localTime,
        alpha: 1 - progress,
        isTransitionOut: true,
      })

      const nextLocalTime = globalTime - nextEntry.globalStart
      active.push({
        entry: nextEntry,
        localTime: nextLocalTime,
        alpha: progress,
      })
      break
    }

    active.push({ entry, localTime, alpha: 1 })
    break
  }

  return { clips: active, transition }
}

export const COLOR_GRADE_FILTERS: Record<string, string> = {
  cinematic_warm: 'sepia(0.12) saturate(1.2) brightness(1.02) contrast(1.08)',
  cinematic_cool: 'hue-rotate(8deg) saturate(0.8) brightness(1.02) contrast(1.08)',
  desaturated: 'saturate(0.35) contrast(1.05)',
  high_contrast: 'contrast(1.35) brightness(0.95) saturate(1.1)',
  vintage: 'sepia(0.2) saturate(0.65) contrast(1.1) brightness(1.02)',
  noir: 'saturate(0) contrast(1.35) brightness(0.88)',
  sepia: 'sepia(0.5) saturate(0.75) contrast(1.05)',
  bw: 'saturate(0) contrast(1.1)',
  bleach_bypass: 'saturate(0.35) contrast(1.35) brightness(1.03)',
  golden_hour: 'sepia(0.15) saturate(1.3) brightness(1.06) hue-rotate(-5deg)',
  moonlight: 'hue-rotate(200deg) saturate(0.25) brightness(0.85) contrast(1.15)',
  film_fade: 'sepia(0.08) saturate(0.55) brightness(1.08) contrast(0.92)',
}

export interface TextOverlay {
  id: string
  text: string
  startTime: number
  duration: number
  position?: string
  style?: string
  animation?: string
  animationDuration?: number
  color?: string
  background?: string
  words?: Array<{ w: string; s: number; e: number }>
  highlightColor?: string
  preset?: string
  fontSize?: number
  fontWeight?: number
  fontFamily?: string
  shadow?: string
  stroke?: string
  strokeWidth?: number
  letterSpacing?: string
  wordAnimation?: string
  glow?: string
  textTransform?: string
  fontStyle?: string
  textStroke?: string
  glowColor?: string
  accentBar?: string
  lineAbove?: boolean
  lineBelow?: boolean
  subtitleText?: string
  subtitleConfig?: string
  eyebrow?: string
  padding?: string
  borderRadius?: number
  maxWidth?: string
  enterAnimation?: string
  exitAnimation?: string
  emphasisAnimation?: string
  enterDuration?: number
  exitDuration?: number
  customCSS?: string
  // Spatial positioning (percentage-based, 0-100) — overrides position string when set
  spatialX?: number
  spatialY?: number
  spatialWidth?: number
  spatialHeight?: number
  spatialRotation?: number
}

export const OVERLAY_POSITION_STYLES: Record<string, React.CSSProperties> = {
  center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' },
  bottom_center: { bottom: '12%', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
  top_center: { top: '8%', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' },
  lower_third: { bottom: '15%', left: '5%', textAlign: 'left' },
  upper_third: { top: '15%', left: '5%', textAlign: 'left' },
  // Title-friendly left-aligned anchors — better than centering for longer
  // titles. Pair with maxWidth so the text wraps into a tidy left-ragged block.
  lower_left: { bottom: '10%', left: '7%', textAlign: 'left' },
  upper_left: { top: '10%', left: '7%', textAlign: 'left' },
  left_center: { top: '50%', left: '7%', transform: 'translateY(-50%)', textAlign: 'left' },
}

export const OVERLAY_STYLE_CLASSES: Record<string, string> = {
  title: 'text-[72px] font-extrabold tracking-wide',
  subtitle: 'text-[48px] font-medium',
  lower_third: 'text-[36px] font-medium bg-black/60 px-6 py-3 rounded',
  caption: 'text-[32px] bg-black/50 px-5 py-2.5 rounded',
  credits: 'text-[42px] font-light tracking-widest text-center whitespace-pre-line',
}
